import React, { useState, useEffect, useRef, useContext } from "react";
import { Container, Row, Col, Button } from "reactstrap";
import { FieldArray, Formik, Form, Field } from "formik";
import { gql, useMutation, useQuery } from "@apollo/client";
import { FormattedMessage, useIntl } from "react-intl";
import { InLinePreLoader } from "../components/preloader";
// import { Radio, RadioGroup } from '../helpers/formik';
// import AddQuestion from '../components/add-question';
import EventContext from "../contexts/event";
import SidebarQuizModerator from "../components/sidebar-quiz-moderator";
// 14/07/2026 Diljith krishna Reason: Added unified Save flow that only persists changed questions/settings and tracks dirty state for both.
import { SidebarQuizSettingsHandle } from "../helpers/quiz-moderator-settings";
import {
  DropDownFormik,
  QuestionInput,
  AnswerInput,
  ReactCommonSwitch,
  QuestionImageUpload,
} from "../helpers/formik";
import Swal from "sweetalert2";
import { Question, QuestionMutation } from "../types/quiz";
import { Presentation } from "../types/presentation";
import { useHistory } from "react-router-dom";
import UserContext from "../contexts/user";

import SideBarBtns from "../components/sidebar-btns";
import LocaleSwitcher from "../components/locale-switcher";
import TopBar from "../components/top-bar-moderator";
import TopModeratorBar from "../components/top-bar-moderator";
import MobileBottomNav from "../components/mobile-bottom-nav";
import QuestionVideoUpload from "../helpers/formik/question-video-upload";
let deleteOptionsIds: (string | undefined)[] = []


// graphQL query to fetch questions
const question_query = gql`
  query question($presentation_id: String) {
    questions: question(
      where: { presentation_id: { _eq: $presentation_id } }
      order_by: { order: asc }
    ) {
      id
      question
      image
      order
      video_id
      video_token
      active_media
      options(order_by: { order: asc }) {
        id
        correct
        option
        order
      }
      visible
    }
  }
`;

// graphQL query to insert questions
const insert_question = `
  insert_question(
    objects: $objects
    on_conflict: {
      constraint: question_pkey
      update_columns: [
        image
        order
        question
        visible
        video_id
        video_token
        active_media
      ]
    }
  ) {
    affected_rows
  }
`;
// graphQL query to delete questions
const delete_question = `
  delete_question(where: {
    _and: [
      {
        presentation_id: {
          _eq: $presentation_id
        }
      }, {
        id: {
          _nin: $delete_question_ids
        }
      }
    ]
  }) {
    affected_rows
  }
`;
// graphQL query to delete question and its options
const delete_question_option = `
delete_question_option(where: {
    _and: [
      {
        id: {
          _in: $delete_option_ids
        }
      }
    ]
  }) {
    affected_rows
  }
`;
// media model for media Liat
export interface Media {
  video: boolean;
  image: boolean;
  videoId: string;
  videoToken: string
}

type QuizFormQuestion = {
  id?: string;
  image?: any;
  order: number;
  question: string;
  video_token: string;
  active_media: string;
  video_id: string | null;
  visible: boolean;
  options: Array<{
    correct: boolean;
    id?: string;
    option: string;
    order: number;
  }>;
};

type SavedQuizQuestion = QuizFormQuestion;

// 14/07/2026 Diljith krishna Reason: Detect only changed questions before save to avoid unnecessary GraphQL mutations.
function getActiveMedia(media?: Media): string {
  if (media?.video) {
    return "video";
  }
  if (media?.image) {
    return "image";
  }
  return "";
}

function areOptionsChanged(
  formOptions: QuizFormQuestion["options"],
  savedOptions: SavedQuizQuestion["options"] = []
): boolean {
  const savedById = new Map(
    savedOptions.filter((option) => option.id).map((option) => [option.id!, option])
  );
  const formOptionIds = new Set(
    formOptions.filter((option) => option.id).map((option) => option.id!)
  );

  if (savedOptions.some((option) => option.id && !formOptionIds.has(option.id))) {
    return true;
  }

  for (let index = 0; index < formOptions.length; index++) {
    const formOption = formOptions[index];

    if (!formOption.id) {
      if (formOption.option?.trim()) {
        return true;
      }
      continue;
    }

    const savedOption = savedById.get(formOption.id);
    if (!savedOption) {
      return true;
    }

    if (
      formOption.option !== savedOption.option ||
      formOption.correct !== savedOption.correct ||
      index + 1 !== savedOption.order
    ) {
      return true;
    }
  }

  return false;
}

function isQuestionChanged(
  formQuestion: QuizFormQuestion,
  savedQuestion: SavedQuizQuestion | undefined,
  questionIndex: number,
  media?: Media
): boolean {
  if (!savedQuestion || !formQuestion.id) {
    return true;
  }

  const activeMedia = getActiveMedia(media);
  const videoToken = media?.videoToken ?? formQuestion.video_token ?? "";

  return (
    formQuestion.question !== savedQuestion.question ||
    (formQuestion.image ?? "") !== (savedQuestion.image ?? "") ||
    formQuestion.visible !== savedQuestion.visible ||
    (formQuestion.video_id ?? "") !== (savedQuestion.video_id ?? "") ||
    videoToken !== (savedQuestion.video_token ?? "") ||
    activeMedia !== (savedQuestion.active_media ?? "") ||
    questionIndex + 1 !== savedQuestion.order ||
    areOptionsChanged(formQuestion.options, savedQuestion.options)
  );
}

function buildQuestionMutationObject(
  question: QuizFormQuestion,
  questionIndex: number,
  presentationId: string,
  media?: Media
): QuestionMutation {
  const { options, ...rest } = question;
  const activeMedia = getActiveMedia(media);

  const object: QuestionMutation = {
    presentation_id: presentationId,
    ...rest,
    video_token: media?.videoToken ?? question.video_token,
    active_media: activeMedia,
    order: questionIndex + 1,
  };

  if (!object.id) {
    delete object.id;
  }

  object.options = {
    data: options.map((questionOption, optionIndex) => {
      const optionPayload = {
        ...questionOption,
        order: optionIndex + 1,
      };

      if (!optionPayload.id) {
        delete optionPayload.id;
      }

      return optionPayload;
    }),
    on_conflict: {
      constraint: "question_option_pkey",
      update_columns: ["correct", "option", "order"],
    },
  };

  return object;
}

function getChangedQuestionIndexes(
  formQuestions: QuizFormQuestion[],
  savedQuestions: SavedQuizQuestion[],
  mediaList: Media[]
): number[] {
  const savedById = new Map(
    savedQuestions.filter((question) => question.id).map((question) => [question.id!, question])
  );

  return formQuestions.reduce<number[]>((changedIndexes, formQuestion, index) => {
    const savedQuestion = formQuestion.id ? savedById.get(formQuestion.id) : undefined;

    if (isQuestionChanged(formQuestion, savedQuestion, index, mediaList[index])) {
      changedIndexes.push(index);
    }

    return changedIndexes;
  }, []);
}

function hasDeletedQuestions(
  formQuestions: QuizFormQuestion[],
  savedQuestions: SavedQuizQuestion[]
): boolean {
  const formQuestionIds = new Set(
    formQuestions.map((question) => question.id).filter(Boolean) as string[]
  );

  return savedQuestions.some(
    (question) => question.id && !formQuestionIds.has(question.id)
  );
}


function hasQuestionFormChanges(
  formQuestions: QuizFormQuestion[],
  savedQuestions: SavedQuizQuestion[],
  mediaList: Media[],
  pendingOptionDeletes: (string | undefined)[]
): boolean {
  if (pendingOptionDeletes.filter(Boolean).length > 0) {
    return true;
  }

  if (hasDeletedQuestions(formQuestions, savedQuestions)) {
    return true;
  }

  return getChangedQuestionIndexes(formQuestions, savedQuestions, mediaList).length > 0;
}

const QuestionDirtyTracker: React.FC<{
  values: { questions: QuizFormQuestion[] };
  savedQuestions: SavedQuizQuestion[];
  mediaList: Media[];
  onDirtyChange: (dirty: boolean) => void;
}> = ({ values, savedQuestions, mediaList, onDirtyChange }) => {
  useEffect(() => {
    onDirtyChange(
      hasQuestionFormChanges(
        values.questions,
        savedQuestions,
        mediaList,
        deleteOptionsIds
      )
    );
  }, [values, savedQuestions, mediaList, onDirtyChange]);

  return null;
};


const QuizModerator: React.FC<{
  presentation: Presentation;
  locale: string;
  forum?: boolean;
  setLocale: (arg0: string) => void;
}> = ({ presentation, locale, setLocale, forum }) => {

  const [showDepFields, setShowDepFields] = useState<number>(0);
  const eventContext = useContext(EventContext);
  const userContext = useContext(UserContext);
  const history = useHistory();
  const intl = useIntl();
  const ansRef = useRef<any>();
  const addQuestionBtnRef = useRef<any>();
  const addAnswerBtnRef = useRef<any>();
  const saveRef = useRef<any>();
  const sidebarSettingsRef = useRef<SidebarQuizSettingsHandle | null>(null);
  const [hasSettingsDirty, setHasSettingsDirty] = useState(false);
  const [hasQuestionDirty, setHasQuestionDirty] = useState(false);
  const showSaveButton = hasSettingsDirty || hasQuestionDirty;
  const [showForumSidebar, setShowForumSidebar] = useState(forum);
  const [mediaList, setMediaList] = useState<Media[]>([]);


  /* callback function to add question from sidebar button click*/
  const addQuestionFromSideBar = (e: any) => {
    // check addQuestionBtnRef is bind to the button
    if (addQuestionBtnRef.current) {
      addQuestionBtnRef.current.onClick();
    }
    // check addQuestionBtnRef is bind to the button
    if (addAnswerBtnRef.current) {
      addAnswerBtnRef.current.onClick();
    }
  };

  /* get questions from graphQL */
  const {
    data,
    loading: queryLoading,
    error,
    refetch
  } = useQuery<{
    questions: Array<Question>;
  }>(question_query, {
    variables: {
      presentation_id: presentation._id,
    },
    skip: !presentation,
  });

  // query for insert question into graphQL
  const [insert_question_mutation, { loading: mutation1Loading }] = useMutation(
    gql`
    mutation insert_question (
      $objects: [question_insert_input!]!
    ) {
      ${insert_question}
    }
    `,
    {
      onCompleted: () => {
        Swal.fire({
          title: "Updated successfully",
          icon: "success",
        });
        refetch();
      },
      onError: (error) => {
        Swal.fire({
          title: "There was an error while updating the questions 1",
          icon: "error",
        });
        console.log("1 delete: error", error);
      },
    }
  );

  // update media list based on saved questions
  useEffect(() => {
    // check data is exist
    if (data && data.questions) {
      let listData: Media[] = [];
      data.questions.forEach(value => {
        listData.push({ video: value?.active_media === "video", image: value?.active_media === "image", videoId: value.video_id == null ? "" : value.video_id, videoToken: value?.video_token });
        // set video/image flags for upload function
        setShowDepFields(0);
      });

      setMediaList(listData);

      console.log("setMediaList", mediaList);
    }

  }, [data])


  // query for question delete and insert in graphQl
  const [insert_and_delete_question_mutation, { loading: mutation2Loading }] =
    useMutation(
      gql`
    mutation insert_and_delete_question (
      $objects: [question_insert_input!]!
      $presentation_id: String
      $delete_question_ids: [uuid!]
    ) {
      ${insert_question}
      ${delete_question}
    }
    `,
      {
        onCompleted: () => {
          // TODO
          Swal.fire({
            title: "Updated successfully",
            icon: "success",
          });
          console.log("Updated successfully", deleteOptionsIds)
          deleteOptionsIds = []
          refetch();
        },
        onError: (error) => {
          Swal.fire({
            title: "There was an error while updating the questions ",
            icon: "error",
          });
          console.log("2 debug: error", error);
        },
        update: (cache, response) => {
          // update cache with query
          cache.writeQuery({
            query: question_query,
            variables: {
              presentation_id: presentation && presentation._id,
            },
            data: {
              questions: response.data.insert_question.returning,
            },
          });
        },
      }
    );


  // delete question option query for graphQL
  const [delete_question_option_mutation] =
    useMutation(
      gql`
    mutation delete_question_options (
      $delete_option_ids: [uuid!]
    ) {
      ${delete_question_option}
    }
    `,
      {
        onCompleted: () => {
          console.log("delete success", deleteOptionsIds);
          deleteOptionsIds = []
        },
        onError: (error) => {
          console.log("2 debug: error", error, deleteOptionsIds);
          deleteOptionsIds = []
        }
      }
    );

  /* save callback function*/
  // 14/07/2026 Diljith krishna Reason: Save only changed questions and/or sidebar settings from a single top-bar action.
  function saveAll() {
    try {
      const savedQuestions = data?.questions ?? [];
      const formQuestions: QuizFormQuestion[] = saveRef.current?.values?.questions ?? [];
      const pendingOptionDeletes = deleteOptionsIds.filter(Boolean);
      const hasQuestionChanges = hasQuestionFormChanges(
        formQuestions,
        savedQuestions,
        mediaList,
        pendingOptionDeletes
      );
      const hasSettingsChanges =
        sidebarSettingsRef.current?.hasUnsavedChanges() ?? false;

      if (!hasQuestionChanges && !hasSettingsChanges) {
        return;
      }

      if (hasSettingsChanges) {
        sidebarSettingsRef.current?.submitSettings();
      }

      if (hasQuestionChanges && saveRef.current) {
        saveRef.current.handleSubmit();
      }
    } catch (error) {
      console.error(error);
    }
  }

  /*function handle DOM mouse down and touch event listeners */
  function useOnClickOutside(ref: any, handler: (arg0: any) => void) {
    useEffect(() => {
      const listener = (event: any) => {
        try {
          // check current ref contain event item
          if (!ref.current || ref.current.contains(event.target)) {
            return;
          }
          handler(event);
        } catch (error) {
          console.error(error);
        }
      };
      // add events listener to the document
      document.addEventListener("mousedown", listener);
      document.addEventListener("touchstart", listener);

      // clear added event
      return () => {
        document.removeEventListener("mousedown", listener);
        document.removeEventListener("touchstart", listener);
      };
    }, [ref, handler]);
  }

  useOnClickOutside(ansRef, () => setShowDepFields(0));


  if (!presentation) {
    return <div />;
  }

  if (error) {
    return (
      <p>
        <i>There was an error</i>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </p>
    );
  }

  return (
    <>
      <SideBarBtns presentation={presentation} />
      <div className="grid-contianer quiz-user-container quiz-mod-container">
        {/*  LocaleSwitcher componets for switch language */}
        <LocaleSwitcher locale={locale} setLocale={setLocale} />
        {/*  TopModeratorBar componets for top bar actions */}
        <TopModeratorBar
          presentation={presentation}
          setShowForumSidebar={setShowForumSidebar}
          forumIsOpen={false}
          saveActioncall={saveAll}
          showSaveButton={showSaveButton}
          tab={4}
          forum={false}
          hasVideo={presentation.video ? true : false}
        />
        {/*  SidebarQuizModerator componets for side nav bar */}
        <SidebarQuizModerator
          ref={sidebarSettingsRef}
          presentation={presentation}
          addQuestionFromSideBar={addQuestionFromSideBar}
          onSettingsDirtyChange={setHasSettingsDirty}
        />
        {/*  Page body componets */}
        <div className="main c-pad-fix">
          <Container>
            <MobileBottomNav presentation={presentation} tab={4} />
            <Row>
              <Col>
                {queryLoading || !data ? (
                  <InLinePreLoader />
                ) : (
                  <Formik
                    innerRef={saveRef}
                    initialValues={{
                      questions: [
                        ...data.questions.map(({ __typename, ...q }) => ({
                          ...q,
                          options: q.options.map(({ __typename, ...qo }) => qo),
                        })),
                      ],
                    }}
                    onSubmit={(values) => {
                      try {
                        const savedQuestions = data?.questions ?? [];
                        const questionsWereDeleted = hasDeletedQuestions(
                          values.questions,
                          savedQuestions
                        );
                        const changedQuestionIndexes = getChangedQuestionIndexes(
                          values.questions,
                          savedQuestions,
                          mediaList
                        );
                        const pendingOptionDeletes = deleteOptionsIds.filter(Boolean);

                        if (
                          !hasQuestionFormChanges(
                            values.questions,
                            savedQuestions,
                            mediaList,
                            pendingOptionDeletes
                          )
                        ) {
                          return;
                        }

                        const questionIndexesToSave = questionsWereDeleted
                          ? values.questions.map((_, index) => index)
                          : changedQuestionIndexes;

                        const objects: Array<QuestionMutation> = questionIndexesToSave.map(
                          (questionIndex) =>
                            buildQuestionMutationObject(
                              values.questions[questionIndex],
                              questionIndex,
                              presentation._id,
                              mediaList[questionIndex]
                            )
                        );

                        if (questionsWereDeleted) {
                          insert_and_delete_question_mutation({
                            variables: {
                              objects,
                              presentation_id: presentation._id,
                              delete_question_ids: objects
                                .map((object) => object.id)
                                .filter(Boolean),
                            },
                          });
                        } else if (objects.length > 0) {
                          insert_question_mutation({
                            variables: {
                              objects,
                            },
                          });
                        }

                        if (pendingOptionDeletes.length > 0) {
                          delete_question_option_mutation({
                            variables: {
                              delete_option_ids: pendingOptionDeletes,
                            },
                          });
                        }
                      } catch (e) {
                        console.log("CHECKSUBMIT end", e);
                      }
                    }}
                    enableReinitialize={true}
                  >
                    {({ values }) => {
                      return (
                        <Form>
                          <QuestionDirtyTracker
                            values={values}
                            savedQuestions={data.questions}
                            mediaList={mediaList}
                            onDirtyChange={setHasQuestionDirty}
                          />
                          {/* Question form components */}
                          <FieldArray
                            name="questions"
                            render={(arrayhelpersFirst) => (
                              <React.Fragment>
                                {values.questions &&
                                  values.questions.length > 0 ? (
                                  values.questions.map((q, qindex) => (
                                    <div
                                      key={qindex}
                                      className={`add-quiz-question-container ${qindex > 0 ? "m-t-60" : "m-t-20"
                                        }`}
                                    >
                                      <Row>
                                        <Col md={6} lg={6} className="ml-auto">
                                          <div className="first-row" style={{ alignItems: 'flex-start' }}>
                                            {/* add class "image-uploaded"  above along with first-row  */}
                                            <Field
                                              name={`questions.${qindex}.visible`}
                                              component={DropDownFormik}
                                              index={qindex}

                                            />
                                            <Field
                                              name={`questions.${qindex}.image`}
                                              component={QuestionImageUpload}
                                              mediaList={mediaList}
                                              index={qindex}
                                              activeStatus={mediaList[qindex]?.image !== undefined ? mediaList[qindex].image : false}
                                              onDataChange={(type: string, value: boolean) => {
                                                // update media list for the added data
                                                setMediaList(prevMediaList => (
                                                  prevMediaList.map((item, index) => (
                                                    index === qindex ? { ...item, ...{ video: value ? false : item.video, image: value } } : item
                                                  ))
                                                ));
                                              }}
                                            />
                                            <Field
                                              name={`questions.${qindex}.video_id`}
                                              component={QuestionVideoUpload}
                                              presentationId={presentation._id}
                                              index={qindex}
                                              mediaList={mediaList}
                                              videoId={values.questions[qindex].video_id}
                                              videoToken={values.questions[qindex].video_token}
                                              activeStatus={mediaList[qindex]?.video !== undefined ? mediaList[qindex].video : false}
                                              onDataChange={(type: string, value: boolean, videoId: string, videoToken: string) => {
                                                // update media list for the added data
                                                setMediaList(prevMediaList => (
                                                  prevMediaList.map((item, index) => (
                                                    index === qindex ? { ...item, ...{ image: value ? false : item.image, video: value, videoId: videoId, videoToken: videoToken } } : item
                                                  ))
                                                ));

                                              }}
                                            />
                                            {qindex !== 0 ? (
                                              <Button
                                                type="button"
                                                onClick={() => {
                                                  arrayhelpersFirst.move(
                                                    qindex,
                                                    qindex - 1
                                                  );
                                                }}
                                                color="link"
                                              >
                                                <img
                                                  src="/icons/oder-up-ico.svg"
                                                  alt=""
                                                />
                                              </Button>
                                            ) : (
                                              <img
                                                className="opacity-0"
                                                src="/icons/oder-up-ico.svg"
                                                alt=""
                                              />
                                            )}
                                            {qindex !==
                                              values.questions.length - 1 ? (
                                              <Button
                                                type="button"
                                                onClick={() => {
                                                  arrayhelpersFirst.move(
                                                    qindex,
                                                    qindex + 1
                                                  );
                                                }}
                                                color="link"
                                              >
                                                <img
                                                  src="/icons/order-down-ico.svg"
                                                  alt=""
                                                />
                                              </Button>
                                            ) : (
                                              <img
                                                className="opacity-0"
                                                src="/icons/order-down-ico.svg"
                                                alt=""
                                              />
                                            )}
                                            <Button
                                              type="button"
                                              onClick={() => {
                                                Swal.fire({
                                                  title:
                                                    "Are You sure you want to remove the question?",
                                                  icon: "warning",
                                                  confirmButtonText: "Yes",
                                                  showCancelButton: true,
                                                }).then((result) => {
                                                  if (result.value) {
                                                    arrayhelpersFirst.remove(
                                                      qindex
                                                    );
                                                    // update media list for the added data
                                                    setMediaList(prevMediaList => (
                                                      prevMediaList.filter((item, index) => index !== qindex)
                                                    ));

                                                  }
                                                });
                                              }}
                                              color="link"
                                            >
                                              <img
                                                src="/icons/trash.svg"
                                                alt=""
                                                style={{ height: '20px', cursor: 'pointer' }}
                                              />
                                            </Button>
                                          </div>
                                        </Col>
                                      </Row>
                                      <div>
                                        <Field
                                          name={`questions.${qindex}.question`}
                                          component={QuestionInput}
                                          placeholder="question text"
                                          index={qindex}
                                          questionCount={
                                            values.questions.length
                                          }
                                        />
                                        <Row>
                                          <Col>
                                            <div className="">
                                              <FieldArray
                                                name={`questions.${qindex}.options`}
                                                render={(arrayHelpers) => (
                                                  <div
                                                    className="answers-container"
                                                    ref={ansRef}
                                                  >
                                                    {values.questions[qindex]
                                                      .options &&
                                                      values.questions[qindex]
                                                        .options.length > 0 ? (
                                                      values.questions[
                                                        qindex
                                                      ].options.map(
                                                        (
                                                          question_option,
                                                          index
                                                        ) => (
                                                          <div
                                                            className={`answer-row-container ${showDepFields ===
                                                              index + 1
                                                              ? "show-dep-fields"
                                                              : "hide-dep-fields"
                                                              }`}
                                                            key={index}
                                                          >
                                                            <div className="answer-container">
                                                              <Row className="align-items-center">
                                                                <Col
                                                                  className="ans-input-container"
                                                                  lg={8}
                                                                  xl={9}
                                                                >
                                                                  <div className="align-ans-btn">
                                                                    <Field
                                                                      onFocus={() =>
                                                                        setShowDepFields(
                                                                          index +
                                                                          1
                                                                        )
                                                                      }
                                                                      correct={
                                                                        values
                                                                          .questions[
                                                                          qindex
                                                                        ]
                                                                          .options[
                                                                          index
                                                                        ]
                                                                          .correct
                                                                      }
                                                                      placeholder="answer"
                                                                      alphabet={String.fromCharCode(
                                                                        index +
                                                                        97
                                                                      )}
                                                                      index={index}
                                                                      name={`questions.${qindex}.options.${index}.option`}
                                                                      component={
                                                                        AnswerInput
                                                                      }
                                                                    />
                                                                    <Button
                                                                      color="primary"
                                                                      className="add-answer-btn"
                                                                      type="button"
                                                                      onClick={() => {
                                                                        // add option data when user click add answer
                                                                        arrayHelpers.insert(
                                                                          index +
                                                                          1,
                                                                          {
                                                                            correct:
                                                                              false,
                                                                            id: "",
                                                                            option:
                                                                              "",
                                                                            order:
                                                                              values
                                                                                .questions[
                                                                                qindex
                                                                              ]
                                                                                .options
                                                                                .length +
                                                                              1,
                                                                          }
                                                                        );
                                                                      }}
                                                                    >
                                                                      <img
                                                                        src="/icons/add-ico.svg"
                                                                        alt=""
                                                                      />{" "}
                                                                      {intl.formatMessage(
                                                                        {
                                                                          id: "quiz.add.answer",
                                                                        }
                                                                      )}
                                                                    </Button>
                                                                  </div>
                                                                </Col>
                                                                <Col
                                                                  className="answer-deps-contianer"
                                                                  lg={4}
                                                                  xl={3}
                                                                >
                                                                  <div className="answer-deps">
                                                                    <Field
                                                                      label={intl.formatMessage(
                                                                        {
                                                                          id: "good.answer",
                                                                        }
                                                                      )}
                                                                      component={
                                                                        ReactCommonSwitch
                                                                      }
                                                                      value={
                                                                        values
                                                                          .questions[
                                                                          qindex
                                                                        ]
                                                                          .options[
                                                                          index
                                                                        ]
                                                                          .correct
                                                                      }
                                                                      name={`questions.${qindex}.options.${index}.correct`}
                                                                    />
                                                                    {index !==
                                                                      0 ? (
                                                                      <Button
                                                                        onClick={() => {
                                                                          arrayHelpers.move(
                                                                            index,
                                                                            index -
                                                                            1
                                                                          );
                                                                        }}
                                                                        type="button"
                                                                        className="order-up"
                                                                        color="link"
                                                                      >
                                                                        <img
                                                                          src="/icons/oder-up-ico.svg"
                                                                          alt=""
                                                                        />
                                                                      </Button>
                                                                    ) : (
                                                                      <img
                                                                        className="opacity-0"
                                                                        src="/icons/oder-up-ico.svg"
                                                                        alt=""
                                                                      />
                                                                    )}
                                                                    {index !==
                                                                      values
                                                                        .questions[
                                                                        qindex
                                                                      ].options
                                                                        .length -
                                                                      1 ? (
                                                                      <Button
                                                                        onClick={() => {
                                                                          arrayHelpers.move(
                                                                            index,
                                                                            index +
                                                                            1
                                                                          );
                                                                        }}
                                                                        type="button"
                                                                        className="order-down"
                                                                        color="link"
                                                                      >
                                                                        <img
                                                                          src="/icons/order-down-ico.svg"
                                                                          alt=""
                                                                        />
                                                                      </Button>
                                                                    ) : (
                                                                      <img
                                                                        className="opacity-0"
                                                                        src="/icons/order-down-ico.svg"
                                                                        alt=""
                                                                      />
                                                                    )}
                                                                    <Button
                                                                      onClick={() => {
                                                                        // delete option id from deleteOptionsIds
                                                                        deleteOptionsIds.push(values
                                                                          .questions[
                                                                          qindex
                                                                        ].options[index].id)
                                                                        // delete option  from questions
                                                                        arrayHelpers.remove(
                                                                          index
                                                                        );
                                                                      }}
                                                                      type="button"
                                                                      className="remove-ans"
                                                                      color="link"
                                                                    >
                                                                      <img
                                                                        src="/icons/trash.svg"
                                                                        alt=""
                                                                        style={{ height: '20px', cursor: 'pointer' }}
                                                                      />
                                                                    </Button>
                                                                  </div>
                                                                </Col>
                                                              </Row>
                                                            </div>
                                                          </div>
                                                        )
                                                      )
                                                    ) : (
                                                      <div className="text-right">
                                                        <Button
                                                          ref={addAnswerBtnRef}
                                                          color="primary"
                                                          className="add-answer-btn btn-commons"
                                                          type="button"
                                                          onClick={() => {

                                                            // add new question field
                                                            arrayHelpers.push({
                                                              correct: false,
                                                              id: "",
                                                              option: "",
                                                              visible: true,
                                                              active_media: "",
                                                              order:
                                                                values
                                                                  .questions[
                                                                  qindex
                                                                ].options
                                                                  .length + 1,
                                                            });


                                                            // update new media list for image/video upload function data
                                                            setMediaList([...mediaList, { video: false, image: false, videoId: "", videoToken: "" }]);
                                                            setShowDepFields(0);
                                                          }}
                                                        >
                                                          <img
                                                            src="/icons/add-ico.svg"
                                                            alt=""
                                                          />{" "}
                                                          Add Answer
                                                        </Button>
                                                      </div>
                                                    )}
                                                  </div>
                                                )}
                                              />
                                              <Button
                                                ref={addAnswerBtnRef}
                                                style={{ display: "none" }}
                                                color="primary"
                                                className="add-answer-btn"
                                                type="button"
                                                onClick={() => {

                                                  // add new question field
                                                  arrayhelpersFirst.insert(
                                                    qindex + 1,
                                                    {
                                                      id: "",
                                                      image: "",
                                                      order: 1,
                                                      question: "",
                                                      active_media: "",
                                                      options: [
                                                        {
                                                          correct: false,
                                                          id: "",
                                                          option: "",
                                                          order:
                                                            values.questions
                                                              .length + 1,
                                                        },
                                                      ],
                                                      visible: true,
                                                    }
                                                  );


                                                  setMediaList([...mediaList, { video: false, image: false, videoId: "", videoToken: "" }]);
                                                }}
                                              >
                                                <img
                                                  src="/icons/add-ico.svg"
                                                  alt=""
                                                />{" "}
                                                Add Answer
                                              </Button>
                                            </div>
                                          </Col>
                                        </Row>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="text-right m-t-20">
                                    <Button
                                      ref={addQuestionBtnRef}
                                      block
                                      color="primary"
                                      className="add-answer-btn btn-commons"
                                      type="button"
                                      onClick={() => {
                                        // add new question field
                                        arrayhelpersFirst.push({
                                          id: "",
                                          image: "",
                                          order: 1,
                                          question: "",
                                          options: [
                                            {
                                              correct: false,
                                              id: "",
                                              option: "",
                                              order:
                                                values.questions.length + 1,
                                            },
                                          ],
                                          visible: true,
                                        });
                                      }}
                                    >
                                      <img src="/icons/add-ico.svg" alt="" />{" "}
                                      <FormattedMessage id="button.add-question" />
                                    </Button>
                                  </div>
                                )}
                              </React.Fragment>
                            )}
                          />
                          <div className="text-right">
                            <Button
                              className="btn-submit btn-commons m-t-60 btn-hide"
                              color="primary"
                              type="submit"
                              style={{ display: 'none' }}
                              disabled={mutation1Loading || mutation2Loading}
                            >
                              <FormattedMessage id="button.save-questions" />
                            </Button>
                          </div>
                        </Form>
                      );
                    }}
                  </Formik>
                )}
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </>
  );
};

export default QuizModerator;
